import React from "react";
import { Check, X, ArrowRight, Database, FileSpreadsheet, Boxes, Sparkles, AlertTriangle, Wallet } from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";
import { COMPARISONS } from "../lib/comparisons";

// 5-column architecture comparison.
// Columns: Capability | Legacy ERP | Spreadsheets + WhatsApp | Stitched SaaS | Scitus.ai
const ROWS = [
    { f: "Real-time cross-functional dashboards",       erp: "partial",            sheets: false,                 stack: "partial",           scitus: true },
    { f: "Live data (vs. end-of-month batch)",          erp: false,                sheets: false,                 stack: "partial",           scitus: true },
    { f: "Multi-store / multi-entity operations view",  erp: "partial",            sheets: false,                 stack: false,               scitus: true },
    { f: "Multi-level approval matrix + audit trail",   erp: "partial",            sheets: false,                 stack: "partial",           scitus: true },
    { f: "Vendor scorecards & price benchmarking",      erp: false,                sheets: false,                 stack: false,               scitus: true },
    { f: "GST-ready invoicing & e-invoice",             erp: true,                 sheets: false,                 stack: "partial",           scitus: true },
    { f: "Audit-grade e-signing built-in",              erp: false,                sheets: false,                 stack: "partial",           scitus: true },
    { f: "HR & people ops on the same login",           erp: false,                sheets: false,                 stack: "partial",           scitus: true },
    { f: "AI copilot grounded in your own data",        erp: false,                sheets: false,                 stack: false,               scitus: true },
    { f: "Mobile-first for on-floor / on-site teams",   erp: false,                sheets: "partial",             stack: "partial",           scitus: true },
    { f: "Implementation time",                         erp: "6–18 months",        sheets: "Forever in progress", stack: "3–6 months",        scitus: "Days, not quarters" },
    { f: "Pricing pattern",                             erp: "₹15–40L upfront + AMC", sheets: "Hidden in payroll",   stack: "5–7 tools, ₹35–60k/mo", scitus: "Per product, bundle = −25%" },
];

const Yes = () => <span style={{ color: "var(--accent)", display: "inline-flex" }}><Check size={18} /></span>;
const No = () => <span style={{ color: "var(--text-dim)", display: "inline-flex" }}><X size={18} /></span>;
const Partial = () => <span style={{ color: "#f0883e", fontSize: "0.78rem", fontWeight: 600 }}>Partial</span>;

const cell = (v) => {
    if (v === true) return <Yes />;
    if (v === false) return <No />;
    if (v === "partial") return <Partial />;
    return <span style={{ fontWeight: 500, color: "var(--text)" }}>{v}</span>;
};

const ERP_CARDS = [
    {
        icon: Database,
        h: "vs. Tally / Zoho Books",
        sub: "You keep the ledger. We replace the spreadsheets.",
        p: "Your CA stays where they are. We replace the WhatsApp approval threads, the indent Excels, the vendor-comparison sheets, the GRN trackers, the manual sign-off chains — and post clean entries back to Tally or Zoho via API. Your books look exactly like they do today, with a fraction of the typing and zero of the chasing.",
    },
    {
        icon: Database,
        h: "vs. SAP B1 / Dynamics / NetSuite",
        sub: "Boardroom-grade ledger stays. Operator-grade workflow moves.",
        p: "Big ERPs are excellent at general ledger, financial consolidation, and statutory reporting. They're punishing at indent → PO → GRN → approval → vendor management → category analytics — which is where store managers, category buyers, project teams, and HR live every day. Scitus moves that workflow off Excel and email, then writes the clean financial transaction back to your ERP. No parallel ledger, no rip-and-replace.",
    },
    {
        icon: Sparkles,
        h: "vs. No ERP — just spreadsheets",
        sub: "Skip the ERP chapter entirely.",
        p: "If you don't already have an ERP, you don't need to sign a ₹25L upfront contract to grow up. Start with one Scitus product. Add the second when it pays for itself. By year two you have the operating spine of a much larger business — without the ERP bill, the ERP consultant retainer, or the ERP go-live war story.",
    },
];

const HONESTY = [
    { h: "We don't replace your ledger.", p: "Your CA still runs Tally, Zoho Books, or SAP. We don't compete with them. We post to them cleanly via API." },
    { h: "We don't do tax filing.", p: "Your tax consultant still files. We make sure every invoice, every GRN, and every payment they need is ready before they ask." },
    { h: "We don't sell email or licences.", p: "We integrate with Google Workspace, Microsoft 365, and your existing SSO. We don't try to bundle in things we're not great at." },
    { h: "We don't ship until it's boardroom-grade.", p: "Anything that goes into a Scitus product must stand up to a CFO and an auditor. If it can't, it doesn't ship." },
];

const TCO = [
    {
        icon: Database,
        tag: "Legacy ERP project",
        cost: "₹15–40L upfront",
        sub: "+ ₹3–8L / year AMC · 6–18 month rollout · 2 dedicated implementers",
        note: "Most SMEs over-buy modules they never operationalise, then spend the next 3 years explaining why the steering committee meeting is on the calendar.",
        accent: false,
    },
    {
        icon: Boxes,
        tag: "Stitched SaaS stack",
        cost: "₹35–60k / month",
        sub: "5–7 best-in-class tools · 1 part-time RevOps · 1 part-time ops admin · brittle integrations",
        note: "Every cross-functional question becomes a data engineering project. The integration layer breaks every quarter.",
        accent: false,
    },
    {
        icon: FileSpreadsheet,
        tag: "Spreadsheets + WhatsApp",
        cost: "₹0 line-item",
        sub: "+ 2 days / week of your senior operator's time · zero audit trail · zero institutional memory",
        note: "The 'free' option silently consumes your most expensive person — and dies the day they take leave.",
        accent: false,
    },
    {
        icon: Sparkles,
        tag: "Scitus.ai (3 products bundled)",
        cost: "₹6–12k / month",
        sub: "−25% bundle discount auto-applied · 2-week rollout · human onboarding included · no implementation contract",
        note: "Start with one. Add the second when it pays for itself. Cancel any month — no annual lock-in.",
        accent: true,
    },
];

export const ComparePage = () => {
    return (
        <section className="section" data-testid="compare-page">
            <div className="container">
                {/* Hero */}
                <div className="section-head">
                    <span className="eyebrow">Compare</span>
                    <h1 className="h1" style={{ marginTop: 18 }} data-testid="compare-h1">
                        We don't replace your ERP.<br />
                        We replace the spreadsheets around it.
                    </h1>
                    <p style={{ maxWidth: 760, margin: "16px auto 0" }}>
                        Most growing businesses don't have an ERP problem. They have an{" "}
                        <strong>operating-workflow</strong> problem — the spreadsheets, WhatsApp threads, and Excel
                        approvals that orbit the ledger. Here's how Scitus.ai compares to the four patterns we walk
                        into most often.
                    </p>
                </div>

                {/* Architecture table — 5 cols */}
                <FadeUpGroup>
                    <div className="compare-table fade-up" style={{ marginTop: 56 }} data-testid="compare-table-page">
                        <table>
                            <thead>
                                <tr>
                                    <th style={{ width: "32%" }}>Capability</th>
                                    <th data-testid="compare-col-erp">
                                        Legacy ERP
                                        <div style={{ fontWeight: 400, fontSize: "0.72rem", color: "var(--text-dim)", marginTop: 4, letterSpacing: 0 }}>
                                            Tally · Zoho Books · SAP B1 · Dynamics · NetSuite
                                        </div>
                                    </th>
                                    <th data-testid="compare-col-sheets">
                                        Spreadsheets + WhatsApp
                                        <div style={{ fontWeight: 400, fontSize: "0.72rem", color: "var(--text-dim)", marginTop: 4, letterSpacing: 0 }}>
                                            The default in most growing SMEs
                                        </div>
                                    </th>
                                    <th data-testid="compare-col-stack">
                                        Stitched SaaS stack
                                        <div style={{ fontWeight: 400, fontSize: "0.72rem", color: "var(--text-dim)", marginTop: 4, letterSpacing: 0 }}>
                                            5–7 point tools held together by Zapier
                                        </div>
                                    </th>
                                    <th data-testid="compare-col-scitus" style={{ color: "var(--accent)" }}>
                                        Scitus.ai
                                        <div style={{ fontWeight: 400, fontSize: "0.72rem", color: "var(--text-dim)", marginTop: 4, letterSpacing: 0 }}>
                                            Standalone products, one platform
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {ROWS.map((r) => (
                                    <tr key={r.f}>
                                        <td>{r.f}</td>
                                        <td>{cell(r.erp)}</td>
                                        <td>{cell(r.sheets)}</td>
                                        <td>{cell(r.stack)}</td>
                                        <td>{cell(r.scitus)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </FadeUpGroup>

                {/* "Already on an ERP" cards */}
                <FadeUpGroup>
                    <div className="fade-up" style={{ marginTop: 88 }}>
                        <h2 className="h2" style={{ textAlign: "center", margin: 0 }} data-testid="compare-erp-heading">
                            Already on an ERP? Here's how we fit.
                        </h2>
                        <p className="muted" style={{ textAlign: "center", maxWidth: 680, margin: "14px auto 0" }}>
                            Scitus.ai is built to sit alongside your ledger, not on top of it. Three patterns, depending
                            on what you already run.
                        </p>
                        <div className="compare-explainers" style={{ marginTop: 36 }} data-testid="compare-erp-cards">
                            {ERP_CARDS.map((c) => (
                                <div
                                    key={c.h}
                                    className="card"
                                    style={{ padding: "26px 26px" }}
                                    data-testid={`compare-erp-card-${c.h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <span
                                        style={{
                                            width: 38,
                                            height: 38,
                                            borderRadius: 10,
                                            background: "var(--accent-soft)",
                                            color: "var(--accent)",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginBottom: 14,
                                        }}
                                    >
                                        <c.icon size={18} />
                                    </span>
                                    <h3 className="h3" style={{ fontSize: "1.1rem", margin: 0 }}>{c.h}</h3>
                                    <p
                                        style={{
                                            color: "var(--accent)",
                                            fontSize: "0.86rem",
                                            margin: "6px 0 12px 0",
                                            fontWeight: 600,
                                            lineHeight: 1.45,
                                        }}
                                    >
                                        {c.sub}
                                    </p>
                                    <p className="muted" style={{ margin: 0, fontSize: "0.92rem", lineHeight: 1.65 }}>
                                        {c.p}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </FadeUpGroup>

                {/* Head-to-head detail comparisons */}
                <FadeUpGroup>
                    <div className="fade-up" style={{ marginTop: 88 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                justifyContent: "center",
                                marginBottom: 14,
                            }}
                        >
                            <span
                                style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 10,
                                    background: "var(--accent-soft)",
                                    color: "var(--accent)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Database size={18} />
                            </span>
                            <h2 className="h2" style={{ margin: 0 }} data-testid="compare-h2h-heading">
                                Head-to-head with your existing ERP.
                            </h2>
                        </div>
                        <p className="muted" style={{ textAlign: "center", maxWidth: 660, margin: "0 auto 32px" }}>
                            Deep, honest comparisons against the ERPs we sit alongside most often. No feature checkbox
                            theatre — just where each tool wins and where Scitus fills the gap.
                        </p>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: 16,
                            }}
                            data-testid="compare-h2h-cards"
                        >
                            {COMPARISONS.map((c) => (
                                <a
                                    key={c.slug}
                                    href={`/compare/${c.slug}`}
                                    className="card"
                                    style={{
                                        padding: "26px 26px",
                                        textDecoration: "none",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 10,
                                    }}
                                    data-testid={`compare-h2h-card-${c.slug}`}
                                >
                                    <span
                                        className="eyebrow"
                                        style={{ color: "var(--accent)", fontSize: "0.74rem" }}
                                    >
                                        {c.category}
                                    </span>
                                    <h3
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontWeight: 700,
                                            color: "var(--text)",
                                            margin: 0,
                                            fontSize: "1.2rem",
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        Scitus.ai vs. {c.name}
                                    </h3>
                                    <p
                                        className="muted"
                                        style={{
                                            margin: 0,
                                            fontSize: "0.9rem",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {c.tagline}
                                    </p>
                                    <span
                                        className="link-arrow"
                                        style={{ marginTop: "auto", fontSize: "0.86rem", color: "var(--accent)" }}
                                    >
                                        Read the comparison →
                                    </span>
                                </a>
                            ))}
                        </div>
                    </div>
                </FadeUpGroup>


                {/* "What we don't do" — honesty */}
                <FadeUpGroup>
                    <div className="fade-up" style={{ marginTop: 88 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                justifyContent: "center",
                                marginBottom: 14,
                            }}
                        >
                            <span
                                style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 10,
                                    background: "var(--accent-soft)",
                                    color: "var(--accent)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <AlertTriangle size={18} />
                            </span>
                            <h2 className="h2" style={{ margin: 0 }} data-testid="compare-honesty-heading">
                                What we don't do.
                            </h2>
                        </div>
                        <p className="muted" style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 32px" }}>
                            Software that promises everything ships nothing. Here's what stays explicitly off our roadmap.
                        </p>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                                gap: 16,
                            }}
                            data-testid="compare-honesty-list"
                        >
                            {HONESTY.map((h) => (
                                <div
                                    key={h.h}
                                    style={{
                                        padding: "20px 22px",
                                        background: "var(--surface)",
                                        border: "1px solid var(--border)",
                                        borderRadius: 12,
                                    }}
                                >
                                    <h4
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontWeight: 700,
                                            color: "var(--text)",
                                            margin: "0 0 8px 0",
                                            fontSize: "0.98rem",
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {h.h}
                                    </h4>
                                    <p className="muted" style={{ margin: 0, fontSize: "0.88rem", lineHeight: 1.6 }}>
                                        {h.p}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </FadeUpGroup>

                {/* TCO comparison */}
                <FadeUpGroup>
                    <div className="fade-up" style={{ marginTop: 88 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                justifyContent: "center",
                                marginBottom: 14,
                            }}
                        >
                            <span
                                style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 10,
                                    background: "var(--accent-soft)",
                                    color: "var(--accent)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Wallet size={18} />
                            </span>
                            <h2 className="h2" style={{ margin: 0 }} data-testid="compare-tco-heading">
                                The honest cost of each approach.
                            </h2>
                        </div>
                        <p className="muted" style={{ textAlign: "center", maxWidth: 660, margin: "0 auto 36px" }}>
                            Numbers below are mid-market SME ranges (50–500 employees) across India and SE Asia. Your
                            quote will depend on scale — but the orders of magnitude don't move.
                        </p>
                        <div
                            className="compare-tco-grid"
                            data-testid="compare-tco-cards"
                        >
                            {TCO.map((t) => (
                                <div
                                    key={t.tag}
                                    className="card"
                                    style={{
                                        padding: "26px 26px",
                                        borderColor: t.accent ? "var(--accent)" : "var(--border)",
                                        position: "relative",
                                        background: t.accent ? "var(--accent-soft)" : "var(--surface)",
                                    }}
                                    data-testid={`compare-tco-${t.tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <span
                                        style={{
                                            width: 38,
                                            height: 38,
                                            borderRadius: 10,
                                            background: t.accent ? "var(--bg)" : "var(--accent-soft)",
                                            color: "var(--accent)",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginBottom: 14,
                                        }}
                                    >
                                        <t.icon size={18} />
                                    </span>
                                    <h3
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontWeight: 700,
                                            color: "var(--text)",
                                            margin: 0,
                                            fontSize: "0.96rem",
                                            letterSpacing: "0.02em",
                                        }}
                                    >
                                        {t.tag}
                                    </h3>
                                    <div
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontWeight: 800,
                                            color: t.accent ? "var(--accent)" : "var(--text)",
                                            fontSize: "1.5rem",
                                            margin: "8px 0 6px 0",
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {t.cost}
                                    </div>
                                    <p className="muted" style={{ margin: "0 0 14px 0", fontSize: "0.85rem", lineHeight: 1.5 }}>
                                        {t.sub}
                                    </p>
                                    <p
                                        style={{
                                            margin: 0,
                                            fontSize: "0.85rem",
                                            lineHeight: 1.55,
                                            color: t.accent ? "var(--text)" : "var(--text-muted)",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        {t.note}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </FadeUpGroup>

                {/* CTA */}
                <div className="cta-banner" style={{ marginTop: 80 }}>
                    <div>
                        <h2 className="h2">Run this comparison on your stack.</h2>
                        <p className="lead mt-4">
                            30-minute working session with an operator on our team. Bring your current stack and an
                            ops bottleneck. We'll show you what would consolidate — and what shouldn't.
                        </p>
                    </div>
                    <a href="/contact" className="btn btn-primary btn-lg" data-testid="compare-cta">
                        Book a Demo <ArrowRight size={18} />
                    </a>
                </div>
            </div>
        </section>
    );
};

export default ComparePage;
